import { useState, useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  rememberBusinessIntent, subscriptionPath, pendingInvite, rememberInvite, forgetInvite,
} from '../../components/School/inviteToken';
import { cleanReferralCode, getReferralCode, clearReferralCode, cleanReferralSource, getReferralSource } from '../../contexts/referralCode';
import { FiMail, FiLock, FiUser, FiAlertCircle, FiCheckCircle, FiEye, FiEyeOff, FiGift, FiUsers } from 'react-icons/fi';
import { getPromoCode, savePromoCode } from '../../config/promoCode';
import './Auth.css';

const Signup = () => {
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signup, refreshEmailVerified } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Trial links in demo follow-up emails carry the CRM lead id
  const leadRef = (searchParams.get('lead') || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 40);
  // Referral links land on /?ref= (remembered in App.jsx) or straight on /signup?ref=
  const referralCode = cleanReferralCode(searchParams.get('ref')) || getReferralCode();
  const [referral, setReferral] = useState(null);
  // Signing up from a school invite (join page link, or one opened earlier)
  const inviteToken = searchParams.get('invite') || pendingInvite();
  const [invite, setInvite] = useState(null);
  // Teachers joining a school through an invite don't need a code
  const showPromoField = !inviteToken;
  const forBusiness = searchParams.get('plan') === 'business';
  const [promoCode, setPromoCode] = useState(() => searchParams.get('promo')?.toUpperCase() || getPromoCode());
  const [promoInfo, setPromoInfo] = useState(null);

  useEffect(() => {
    if (!inviteToken) return;
    rememberInvite(inviteToken);
    httpsCallable(functions, 'getSchoolInvite')({ token: inviteToken })
      .then((res) => {
        if (!res.data?.valid) return;
        setInvite(res.data);
        setFormData((f) => (f.email ? f : { ...f, email: res.data.email }));
      })
      .catch(() => {});
  }, [inviteToken]);

  useEffect(() => {
    if (!referralCode) return;
    httpsCallable(functions, 'checkReferralCode')({ code: referralCode })
      .then((res) => { if (res.data?.valid) setReferral(res.data); })
      .catch(() => {});
  }, [referralCode]);

  /* Hide the shared Layout header/footer so the auth screen gets the same
     self-contained, branded shell as the new logged-out homepage. */
  useLayoutEffect(() => {
    document.body.classList.add('auth-active');
    return () => document.body.classList.remove('auth-active');
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }

    if (formData.password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    setLoading(true);

    // Check the code first, so a typo can be fixed before the account exists
    const enteredCode = showPromoField ? promoCode.trim() : '';
    if (enteredCode) {
      try {
        const res = await httpsCallable(functions, 'checkPromoCode')({ code: enteredCode });
        setPromoInfo(res.data);
      } catch (promoErr) {
        setLoading(false);
        return setError(`${promoErr.message || 'That code is not valid'}. Check it, or leave the box empty to continue without one.`);
      }
    }

    // The invite was emailed to this address, so signing up with it proves the inbox:
    // no verification email, and they join the school straight away
    const joiningSchool = !!invite && formData.email.trim().toLowerCase() === invite.email;

    try {
      await signup(formData.email, formData.password, formData.displayName, leadRef, referral ? referralCode : '',
        referral ? cleanReferralSource(searchParams.get('src')) || getReferralSource() : '',
        { skipVerificationEmail: joiningSchool });
      clearReferralCode();
      if (searchParams.get('plan') === 'business') rememberBusinessIntent();
      if (enteredCode) {
        savePromoCode(enteredCode);
        // Kept on the account too, as they may verify their email on another device
        await httpsCallable(functions, 'checkPromoCode')({ code: enteredCode, remember: true, business: forBusiness }).catch(() => {});
      }

      if (joiningSchool) {
        try {
          const res = await httpsCallable(functions, 'schoolJoinOnSignup')({ token: inviteToken });
          if (res.data?.joined) {
            forgetInvite();
            await refreshEmailVerified();
            navigate('/pathways');
            return;
          }
        } catch (joinErr) {
          console.error('Joining the school after signup failed:', joinErr);
        }
        // Couldn't join automatically: fall back to the usual verify-then-join flow
        await httpsCallable(functions, 'sendVerificationEmail')().catch(() => {});
      }
      navigate(subscriptionPath()); // Plan selection / free trial, or back to a school invite
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError('Failed to create account. Please try again.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Sign Up - TeachESLPro | Start Teaching Better Today</title>
        <meta name="description" content="Create your TeachESLPro account and get instant access to high-quality ESL teaching resources. Join hundreds of teachers saving time on lesson planning." />
      </Helmet>


      <main className="auth-main">
        <div className="auth-shell">
          <div className="auth-head">
            <h1>Start teaching <em>better today</em></h1>
            <p>Create your account and get instant access</p>
          </div>

          <div className="auth-card">
            {invite && (
              <div className="auth-info" style={{ marginTop: 0, marginBottom: 20 }}>
                <FiUsers className="w-5 h-5" />
                <span>
                  You&apos;re joining <strong>{invite.schoolName}</strong>. Sign up with <strong>{invite.email}</strong> and
                  you&apos;ll join straight away, with no verification email needed.
                </span>
              </div>
            )}
            {referral && (
              <div className="auth-info" style={{ marginTop: 0, marginBottom: 20 }}>
                <FiGift className="w-5 h-5" />
                <span>
                  {referral.referrerName ? `${referral.referrerName} invited you.` : "You've been invited."}
                  {/* Affiliate links keep the standard 7-day trial */}
                  {referral.trialDays > 7 && <> Your free trial is <strong>{referral.trialDays} days</strong> instead of 7.</>}
                </span>
              </div>
            )}

            {error && (
              <div className="auth-error">
                <FiAlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-field">
                <label htmlFor="displayName" className="auth-label">Full name</label>
                <div className="auth-input-wrap">
                  <span className="auth-input-icon"><FiUser /></span>
                  <input
                    id="displayName"
                    name="displayName"
                    type="text"
                    value={formData.displayName}
                    onChange={handleChange}
                    className="auth-input"
                    placeholder="John Smith"
                    required
                  />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="email" className="auth-label">Email address</label>
                <div className="auth-input-wrap">
                  <span className="auth-input-icon"><FiMail /></span>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="auth-input"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="password" className="auth-label">Password</label>
                <div className="auth-input-wrap">
                  <span className="auth-input-icon"><FiLock /></span>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    className="auth-input has-toggle"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="auth-toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="confirmPassword" className="auth-label">Confirm password</label>
                <div className="auth-input-wrap">
                  <span className="auth-input-icon"><FiLock /></span>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="auth-input has-toggle"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="auth-toggle"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              {showPromoField && (
                <div className="auth-field">
                  <label htmlFor="promoCode" className="auth-label">Promo code (optional)</label>
                  <div className="auth-input-wrap">
                    <span className="auth-input-icon"><FiGift /></span>
                    <input
                      id="promoCode"
                      type="text"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoInfo(null); }}
                      maxLength={30}
                      autoComplete="off"
                      placeholder="Enter your code"
                      className="auth-input"
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="auth-btn auth-btn-primary auth-submit"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </form>

            <div className="auth-info">
              <FiCheckCircle className="w-5 h-5" />
              <span>
                {forBusiness && promoCode.trim()
                  ? 'If your code gives your school free days, you can start them once you\u2019ve verified your email and named your school, with no card needed.'
                  : promoInfo?.type === 'trial' || promoCode.trim()
                  ? 'If your code gives you free Premium, it starts as soon as you verify your email, with no card needed.'
                  : `After signing up, start your ${referral?.trialDays || 7}-day free trial on the Standard plan, or subscribe immediately to any plan.`}
              </span>
            </div>

            <p className="auth-alt">
              Already have an account?{' '}
              <Link to="/login">Log in</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Signup;
